import './styles.css';
import * as THREE from 'three';
import { GameState } from './src/game/GameState.js';
import { DriveStateMachine } from './src/game/DriveStateMachine.js';
import { GameUI } from './src/ui/GameUI.js';
import { YouTubePlayer } from './src/audio/YouTubePlayer.js';
import { MenuLoopPlayer } from './src/audio/MenuLoopPlayer.js';
import { EngineSound } from './src/audio/EngineSound.js';
import { RevSound } from './src/audio/RevSound.js';
import { SFXManager } from './src/audio/SFXManager.js';
import { COLOR_PALETTES, getPhaseName } from './src/world/ColorPalettes.js';
import { BiomeManager } from './src/world/BiomeManager.js';
import { PoolManager } from './src/systems/pooling/PoolManager.js';
import { BirdManager } from './src/entities/Bird.js';
import { PlaneManager } from './src/entities/Plane.js';
import { PackGenerator } from './src/game/PackGenerator.js';
import { ItemGenerator } from './src/game/ItemGenerator.js';
import { WaveSpawnSystem } from './src/game/WaveSpawnSystem.js';
import { StatCalculator } from './src/game/StatCalculator.js';
import { EquipmentManager } from './src/items/EquipmentManager.js';
import { DebugMenu } from './src/ui/DebugMenu.js';
import { WavePickupManager } from './src/world/WavePickupManager.js';
import { SignManager } from './src/entities/SignManager.js';
import { WAVE_SIGN_TYPE } from './src/entities/signTypes/index.js';
import { OfflineSimulator } from './src/game/OfflineSimulator.js';
import { ModalManager } from './src/ui/ModalManager.js';
import { ScenePreloader } from './src/systems/ScenePreloader.js';

// Firebase cloud services
import { isConfigured as firebaseConfigured } from './src/firebase/config.js';
import { AccountManager } from './src/firebase/AccountManager.js';
import { CloudSaveManager } from './src/firebase/CloudSaveManager.js';
import { LeaderboardManager } from './src/firebase/LeaderboardManager.js';
import { FriendManager } from './src/firebase/FriendManager.js';
import { SaveSlotService } from './src/game/SaveSlotService.js';
import { SyncCoordinator } from './src/services/SyncCoordinator.js';


// Centralized configuration constants
import {
    CAMERA,
    RENDERING,
    FOG,
    LIGHTING,
    SKY,
    STARS,
    SUN,
    CLOUDS,
    COLOR_CYCLE,
    CAR,
    EQUIPMENT,
    BIRDS,
    PLANES,
    TIMING,
    WORLD,
} from './src/config/constants.js';

// Rendering managers
import { SkyManager } from './src/rendering/SkyManager.js';
import { StarFieldManager } from './src/rendering/StarFieldManager.js';
import { SunManager } from './src/rendering/SunManager.js';
import { CloudManager } from './src/rendering/CloudManager.js';
import { LightingManager } from './src/rendering/LightingManager.js';
import { ColorCycleManager } from './src/rendering/ColorCycleManager.js';
import { WorldTranslator } from './src/systems/WorldTranslator.js';
import { CameraController } from './src/systems/CameraController.js';
import { CarManager } from './src/entities/CarManager.js';
import { DebugPanel } from './src/ui/DebugPanel.js';

// Refactored managers (Phase 1-3 extraction)
import { StartScreenManager, setupZoomPrevention } from './src/ui/StartScreenManager.js';
import { OfflineDisplayManager } from './src/ui/OfflineDisplayManager.js';
import { EventManager } from './src/systems/EventManager.js';

// Timer consolidation (Phase 6)
import { uiUpdateScheduler, getUIUpdateScheduler } from './src/systems/index.js';

// Event system for cross-module communication
import { eventBus, GameEvents } from './src/utils/EventBus.js';

// Bug reporting
import { setupBugReportButtons } from './src/ui/BugReportUI.js';
import { setupConsoleErrorCapture } from './src/services/BugReportManager.js';

// Main app class
class RoadTripApp {
    constructor(configs) {
        this.scene = null;
        this.camera = null;
        this.renderer = null;

        // Object pooling system
        this.poolManager = null;

        // Biome system (handles road, terrain, and roadside objects)
        this.biomeManager = null;

        // Wave pickup manager (3D pickups on road)
        this.wavePickupManager = null;

        // Camera/movement settings
        this.speed = 0;
        this.distance = 0;
        this.travelDistance = 0; // Distance along the path

        // World translator - moves the world past the stationary camera
        this.worldTranslator = null;

        // Debug logging for long-running diagnostics
        this.debugLogInterval = null;
        this.debugLogHistory = [];

        // Debug panel (visible GUI for world translation monitoring)
        // Enable via iddqd debug menu -> Display Options -> Show World Debug Panel
        this.debugPanel = new DebugPanel({ enabled: false });

        // Promise that resolves when all async initialization is complete
        // Used by initApp() to wait before starting offline recovery
        this.ready = new Promise(resolve => {
            this._resolveReady = resolve;
        });

        // IDLE CLICKER GAME STATE (using modules)
        this.gameState = new GameState(configs.upgrades, configs.cars);

        // Listen for prestige events to reset biome
        eventBus.on(GameEvents.PRESTIGE_PERFORMED, () => {
            if (this.biomeManager) {
                console.log('[Main] Prestige detected - resetting biome to initial state');
                this.biomeManager.resetBiome(0);
            }
        });

        this.itemsData = null; // Will be loaded from items.json (for colors/milestones)
        this.itemGenerator = null; // Will be created from item-generation-config.json
        this.packGenerator = null; // Will be created after items load
        this.statCalculator = null; // Will be created after items load
        this.debugMenu = null; // Debug menu (type "iddqd" to open)
        this.equipmentManager = null; // Will be created after car loads
        this.driveStateMachine = new DriveStateMachine(this.gameState);
        this.gameUI = null; // Created after DOM is ready in init()
        this.gameStarted = false; // Set to true when start button is clicked

        // Initialize offline display manager
        this.offlineDisplayManager = new OfflineDisplayManager(this.gameState);

        // Firebase cloud services (optional - only if configured)
        this.accountManager = null;
        this.cloudSaveManager = null;
        this.leaderboardManager = null;
        this.friendManager = null;
        this._initFirebase();

        // Background tab handling - keep game running when backgrounded
        this.isUsingFallbackTimer = false;
        this.fallbackTimerInterval = null;

        // Offline simulation (runs on main menu when game is unloaded)
        this.offlineSimulator = null;

        // Refactored managers (Phase 1-3 extraction)
        this.eventManager = null; // Initialized in init() after scene

        // Scene preloading state (for fresh scene on return from offline)
        this._scenePreloading = false;
        this._sceneReady = true; // True initially since scene is ready after first init

        // Camera controller (initialized in init after scene/camera are ready)
        this.cameraController = null;

        // Mobile detection for reduced draw distance
        this.isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

        // Fog/visibility controls (from constants, reduced on mobile)
        this.fogNear = this.isMobile ? FOG.NEAR_MOBILE : FOG.NEAR;
        this.fogFar = this.isMobile ? FOG.FAR_MOBILE : FOG.FAR;

        // Chunk count (reduced on mobile)
        this.chunkCount = this.isMobile ? WORLD.CHUNK_COUNT_MOBILE : WORLD.CHUNK_COUNT;

        // Color cycle manager (handles day/night cycle and color transitions)
        this.colorCycleManager = new ColorCycleManager({
            colorPalettes: COLOR_PALETTES,
            enableColorCycle: COLOR_CYCLE.ENABLED,
            startingPaletteIndex: 0
        });

        // Equipment slot positions (from constants)
        // All positions are in car-local space: X (left/right), Y (up/down), Z (forward/back)
        // NOTE: Car is rotated 180°, so positive Z = toward camera/front
        this.dashboardSlotPosition = { ...EQUIPMENT.SLOTS.DASHBOARD };
        this.keychainSlotPosition = { ...EQUIPMENT.SLOTS.KEYCHAIN };
        this.rearviewSlotPosition = { ...EQUIPMENT.SLOTS.REARVIEW };

        // Equipment item scale (from constants)
        this.itemPrimitiveScale = EQUIPMENT.ITEM_SCALE;

        // Rendering managers (initialized in init)
        this.skyManager = null;
        this.starFieldManager = null;
        this.sunManager = null;
        this.cloudManager = null;
        this.lightingManager = null;

        // Scene object references (set during init)
        this.birdManager = null; // Managed by BirdManager
        this.planeManager = null; // Managed by PlaneManager
        this.carManager = null; // Managed by CarManager (initialized in init)
        this.signManager = null; // Managed by SignManager

        // Bird and plane managers will be initialized during init()

        // AUDIO SYSTEMS (using modules)
        this.youtubePlayer = new YouTubePlayer('4xDzrJKXOOY');
        this.menuLoopPlayer = new MenuLoopPlayer();
        this.sfxManager = new SFXManager(this.gameState);
        // Note: Engine/rev sounds use SFXManager's audio context (not audioListener)

        // Time tracking for color cycle
        this.lastTime = performance.now();
        this.lastUIUpdateTime = 0; // Throttle UI updates for performance

        // Initialize color cycle manager with game state
        this.colorCycleManager.setDependencies({ gameState: this.gameState });
        this.colorCycleManager.calculateStartingPaletteFromGameTime();
        this.colorCycleManager.setLastSubCycleTime(this.gameState.state.gameTime);
        this.colorCycleManager.applyStartingPalette();

        this.init();

        // Set up event manager (replaces setupEventListeners)
        this.eventManager = new EventManager(this);
        this.eventManager.setup();

        this.animate();
    }

    init() {
        // Scene setup
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x000000); // Black fallback (covered by sky sphere)
        this.scene.fog = new THREE.Fog(this.colorCycleManager.fogColor, this.fogNear, this.fogFar);

        // Camera setup (using constants)
        this.camera = new THREE.PerspectiveCamera(
            CAMERA.FOV,
            window.innerWidth / window.innerHeight,
            CAMERA.NEAR_PLANE,
            CAMERA.FAR_PLANE
        );
        this.camera.position.set(
            CAMERA.INITIAL_POSITION.x,
            CAMERA.INITIAL_POSITION.y,
            CAMERA.INITIAL_POSITION.z
        );
        this.camera.lookAt(
            CAMERA.INITIAL_LOOK_AT.x,
            CAMERA.INITIAL_LOOK_AT.y,
            CAMERA.INITIAL_LOOK_AT.z
        );
        this.scene.add(this.camera);

        // Audio listener setup (for Web Audio API)
        this.audioListener = new THREE.AudioListener();
        this.camera.add(this.audioListener);

        // Renderer setup - Resolution based on native display scaled by RESOLUTION_SCALE
        this.renderer = new THREE.WebGLRenderer({ antialias: false });
        this.renderer.setPixelRatio(window.devicePixelRatio); // Render at device resolution to reduce aliasing

        const aspectRatio = window.innerWidth / window.innerHeight;
        const renderWidth = Math.round(window.innerWidth * RENDERING.RESOLUTION_SCALE);
        const renderHeight = Math.round(window.innerHeight * RENDERING.RESOLUTION_SCALE);

        this.renderer.setSize(renderWidth, renderHeight, false);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Soft shadows reduce acne artifacts

        // Scale canvas to fill window with CSS
        this.renderer.domElement.style.width = '100%';
        this.renderer.domElement.style.height = '100vh';
        this.renderer.domElement.style.imageRendering = 'pixelated';

        document.body.appendChild(this.renderer.domElement);

        // Handle WebGL context loss (can happen after long mobile suspension)
        this.renderer.domElement.addEventListener('webglcontextlost', (event) => {
            console.error('[Main] WebGL context lost');
            event.preventDefault(); // Allow context to be restored
        });

        this.renderer.domElement.addEventListener('webglcontextrestored', () => {
            console.log('[Main] WebGL context restored - reinitializing');
            // Reinitialize materials and textures that may have been lost
            if (this.biomeManager) {
                this.biomeManager.updateMiles(this.gameState.getTotalMiles());
            }
        });

        // Initialize lighting manager (handles ambient, directional, and headlights)
        this.lightingManager = new LightingManager(this.scene, this.camera);
        this.lightingManager.setAmbient(this.colorCycleManager.ambientLightColor, this.colorCycleManager.ambientLightIntensity);
        this.lightingManager.setDirectional(this.colorCycleManager.directionalLightColor, this.colorCycleManager.directionalLightIntensity);
        this.lightingManager.initialize();

        // Initialize sky manager
        this.skyManager = new SkyManager(this.scene, this.camera);
        this.skyManager.setColors(this.colorCycleManager.skyColorTop, this.colorCycleManager.skyColorBottom);
        this.skyManager.initialize();

        // Initialize star field manager
        this.starFieldManager = new StarFieldManager(this.scene, this.camera);
        this.starFieldManager.initialize();

        // Initialize sun manager
        this.sunManager = new SunManager(this.scene, this.camera);
        this.sunManager.setColor(this.colorCycleManager.sunColor);
        this.sunManager.initialize();

        // Initialize world translator - moves world past stationary camera
        // All world objects should be added to worldTranslator instead of scene
        // Must be initialized before any managers that use it
        this.worldTranslator = new WorldTranslator(this.scene);
        window.worldTranslator = this.worldTranslator;

        // Initialize cloud manager (with reduced settings on mobile)
        this.cloudManager = new CloudManager(this.scene, this.camera, this.worldTranslator, {
            fogFar: this.fogFar,
            isMobile: this.isMobile
        });
        this.cloudManager.setColorPalettes(COLOR_PALETTES);
        this.cloudManager.setPaletteIndex(this.colorCycleManager.getCurrentPaletteIndex());
        this.cloudManager.initialize();

        // Initialize object pooling system, then biome system
        this.poolManager = new PoolManager();
        this.poolManager.initialize('data/roadside-objects.json').then(() => {
            // Initialize biome system (handles road, terrain, and roadside objects)
            this.biomeManager = new BiomeManager(this.scene, this.camera, this.worldTranslator, {
                chunkCount: this.chunkCount
            });
            this.biomeManager.setPoolManager(this.poolManager);

            const startingMiles = this.gameState.getTotalMiles();

            this.biomeManager.initialize(startingMiles).then(async () => {
            // Set color cycle manager dependencies now that managers are ready
            this.colorCycleManager.setDependencies({
                biomeManager: this.biomeManager,
                cloudManager: this.cloudManager,
                lightingManager: this.lightingManager,
                skyManager: this.skyManager,
                sunManager: this.sunManager,
                scene: this.scene
            });

            // Set reverse reference so BiomeManager can notify ColorCycleManager on biome change
            this.biomeManager.setColorCycleManager(this.colorCycleManager);

            // Set initial palette with time index for biome-specific colors
            const currentPaletteIndex = this.colorCycleManager.getCurrentPaletteIndex();
            this.biomeManager.setPalette(COLOR_PALETTES[currentPaletteIndex], currentPaletteIndex);

            // Initialize camera controller
            this.cameraController = new CameraController(this.camera, this.scene, {
                height: CAMERA.HEIGHT,
                lookAtDistance: CAMERA.LOOK_AT_DISTANCE,
                yawSmoothing: CAMERA.YAW_SMOOTHING,
                pitchSmoothing: CAMERA.PITCH_SMOOTHING,
                yawOffset: CAMERA.YAW_OFFSET,
                maxPitch: CAMERA.MAX_PITCH,
                lateralOffset: CAMERA.LATERAL_OFFSET
            });
            this.cameraController.setDependencies({
                biomeManager: this.biomeManager,
                worldTranslator: this.worldTranslator,
                lightingManager: this.lightingManager
            });
            this.cameraController.initializeFromRoad();

            // Initialize bird manager (using constants)
            this.birdManager = new BirdManager(this.scene, this.camera, this.worldTranslator, {
                enabled: BIRDS.ENABLED,
                count: BIRDS.COUNT,
                size: BIRDS.SIZE,
                minHeight: BIRDS.MIN_HEIGHT,
                maxHeight: BIRDS.MAX_HEIGHT,
                minSpeed: BIRDS.MIN_SPEED,
                maxSpeed: BIRDS.MAX_SPEED,
                fadeThreshold: BIRDS.FADE_THRESHOLD,
                fogFar: this.fogFar
            });
            this.birdManager.initialize();

            // Initialize plane manager (using constants)
            this.planeManager = new PlaneManager(this.scene, this.camera, this.worldTranslator, {
                enabled: PLANES.ENABLED,
                size: PLANES.SIZE,
                minHeight: PLANES.MIN_HEIGHT,
                maxHeight: PLANES.MAX_HEIGHT,
                minSpawnDistance: PLANES.MIN_SPAWN_DISTANCE,
                maxSpawnDistance: PLANES.MAX_SPAWN_DISTANCE,
                speed: PLANES.SPEED,
                minSpawnInterval: PLANES.MIN_SPAWN_INTERVAL,
                maxSpawnInterval: PLANES.MAX_SPAWN_INTERVAL,
                maxCount: PLANES.MAX_COUNT
            });
            this.planeManager.initialize();

            // Initialize wave pickup manager
            this.wavePickupManager = new WavePickupManager(
                this.scene,
                this.camera,
                this.biomeManager,
                this.gameState,
                this.worldTranslator,
                this.sfxManager
            );
            this.wavePickupManager.initialize();

            // Set up callback for when pickups are collected
            this.wavePickupManager.onPickupCollected = (packData) => {
                this.handleWavePickupCollected(packData);
            };

            // Set up callback for auto-collect pickups (salvage crates)
            this.wavePickupManager.onAutoCollect = (pickupType, result, packData) => {
                this.handleAutoCollect(pickupType, result, packData);
            };

            // Initialize sign manager for road signs
            // Register sign types before initialization so first sign can spawn
            this.signManager = new SignManager(
                this.scene,
                this.camera,
                this.biomeManager,
                this.worldTranslator,
                this.gameState
            );
            this.signManager.setLightingManager(this.lightingManager);
            this.signManager.registerSignType(WAVE_SIGN_TYPE);
            this.signManager.setDefaultSignType('highway');
            await this.signManager.initialize();

            // Initialize car manager
            this.carManager = new CarManager(this.camera, {
                basePosition: CAR.BASE_POSITION
            });
            this.carManager.setDependencies({
                biomeManager: this.biomeManager,
                worldTranslator: this.worldTranslator
            });
            // Set callback for when car is loaded
            this.carManager.onCarLoaded = (car) => {
                // Create equipment manager for 3D items
                this.equipmentManager = new EquipmentManager(this.gameState, this.itemsData, car);

                // Apply configuration from main settings
                this.equipmentManager.slotPositions = {
                    dashboard: this.dashboardSlotPosition,
                    keychain: this.keychainSlotPosition,
                    rearview: this.rearviewSlotPosition
                };
                this.equipmentManager.itemScale = this.itemPrimitiveScale;

                // Create the slots
                this.equipmentManager.createSlots();
            };
            // Load car model
            this.carManager.loadCar();

            // Load items data and initialize pack system (must await to ensure statCalculator exists)
            await this.loadItemsData();

            // Create engine and rev sounds (they use SFXManager's audio context)
            // Note: actual loading deferred until sfxManager.initAudioContext() is called on user interaction
            this.engineSound = new EngineSound(this.sfxManager);
            this.revSound = new RevSound(this.sfxManager);
            this.engineSoundReady = false;
            this.revSoundReady = false;

            // Register sounds with SFXManager
            this.sfxManager.setEngineSound(this.engineSound);
            this.sfxManager.setRevSound(this.revSound);

            // Create UI (using GameUI module) - pass null for musicPlayer (YouTube player is controlled from start screen)
            this.gameUI = new GameUI(this.gameState, this.driveStateMachine, null, this.sfxManager, this);
            this.driveStateMachine.setGameUI(this.gameUI); // Pass GameUI reference for sound effects

            // Wire up offline display manager with gameUI modals and unload callback
            if (this.offlineDisplayManager) {
                this.offlineDisplayManager.gameUIModals = this.gameUI.modals;
                this.offlineDisplayManager.onUnloadGame = () => this.unloadGame();
            }

            // Update progress: 80% (assets loaded, UI ready)
            // Final 100% is called from initApp() after offline recovery completes
            if (window.updateLoadingProgress) window.updateLoadingProgress(80);

            // Don't start automatically - wait for start button
            // Store reference to start function globally
            window.startGame = () => {
                this.startFadeInAndMusic();
            };

            // Signal that async initialization is complete
            if (this._resolveReady) {
                this._resolveReady();
            }
        }); // End biomeManager.initialize().then()
        }); // End poolManager.initialize().then()
    }

    /**
     * Initialize auth state and select the correct save slot.
     * This MUST be called after RoadTripApp is constructed but BEFORE
     * accessing any game state that depends on the correct slot.
     *
     * The flow is:
     * 1. Wait for AccountManager to determine auth state (if Firebase configured)
     * 2. Select the correct slot (account or anonymous)
     * 3. Initialize GameState with data from that slot
     *
     * @returns {Promise<{slot: string, isLinked: boolean}>} The selected slot info
     */
    async initializeAuthAndSlot() {
        console.log('[Init] === INITIALIZING AUTH AND SLOT ===');

        // If Firebase is not configured, use anonymous slot
        if (!this.accountManager) {
            console.log('[Init] No Firebase - using anonymous slot');
            this.gameState.saveManager.switchToAnonymous();
            this.gameState.initializeState();
            return { slot: 'anonymous', isLinked: false };
        }

        // Wait for AccountManager to resolve auth state
        // This ensures we know if user is logged in before proceeding
        console.log('[Init] Waiting for auth state...');
        await this.accountManager.waitForReady();

        // Determine if user is linked (logged in with real account)
        const isLinked = this.accountManager.isLinked();
        console.log('[Init] Auth resolved - isLinked:', isLinked);

        // Select the correct slot based on auth state
        if (isLinked) {
            this.gameState.saveManager.switchToAccount();
            this.saveSlotService?.setLastActiveSlot('account');
            console.log('[Init] Selected ACCOUNT slot');
        } else {
            this.gameState.saveManager.switchToAnonymous();
            this.saveSlotService?.setLastActiveSlot('anonymous');
            console.log('[Init] Selected ANONYMOUS slot');
        }

        // NOW initialize the game state from the correct slot
        this.gameState.initializeState();

        // Update driver display name
        if (this.saveSlotService) {
            this.saveSlotService._updateDriverDisplayName({
                user: this.accountManager.currentUser,
                isAnonymous: !isLinked
            });
        }

        const slot = isLinked ? 'account' : 'anonymous';

        // Mark that initial auth/slot setup is complete
        // This prevents the onAuthStateChanged handler from re-processing
        window._authSlotInitialized = true;

        console.log('[Init] === AUTH AND SLOT COMPLETE ===', { slot, isLinked });

        return { slot, isLinked };
    }

    async startFadeInAndMusic() {
        // Get fade overlay element
        const fadeOverlay = document.getElementById('fade-overlay');

        // Simple auth check - no session management needed
        if (this.accountManager?.isLinked()) {
            console.log('[Main] User is logged in, starting game...');
        } else {
            console.log('[Main] Anonymous user, starting game...');
        }

        // Enable game loop and restart animation
        this.gameStarted = true;
        this.lastTime = performance.now(); // Reset timing to avoid huge delta
        requestAnimationFrame(() => this.animate());

        // Start periodic cloud sync during active gameplay (every 60s)
        this._startActiveGameplaySync();

        // Initialize momentum tracking
        const currentTime = performance.now() / 1000;
        this.driveStateMachine.lastInteractionTime = currentTime;
        this.driveStateMachine.momentumBonus = 0;

        // Always restore momentum from session, regardless of time away
        // Use setRestoredMomentum() for more reliable cross-browser behavior
        const savedMomentum = this.gameState.getRestoredMomentum();
        if (savedMomentum > 0) {
            this.driveStateMachine.setRestoredMomentum(savedMomentum);
        }

        // Check for offline progress and apply it directly
        const offlineProgress = this.gameState.calculateOfflineProgress();

        if (offlineProgress) {
            // Apply offline progress immediately
            this.gameState.applyOfflineProgress(offlineProgress);

            // Check if tutorial wave was collected offline and needs to show tutorial
            const waves = this.gameState.state.waves;
            if (waves?.tutorialCollectedOffline && !waves?.tutorialShown) {
                // Clear the offline flag
                waves.tutorialCollectedOffline = false;
                this.gameState.save();

                // Queue the tutorial modal to show after game starts
                this.gameUI.modals.queue(() => {
                    this.gameUI.showWaveTutorialModal();
                    return { close: () => {} }; // Return close function for queue system
                });
            }
        }

        // Continue with normal game start
        this.continueGameStart(fadeOverlay);
    }

    continueGameStart(fadeOverlay) {
        // Guard against duplicate calls (can happen in edge cases like fuel depletion race)
        if (this._gameStartInProgress) {
            console.warn('[Main] continueGameStart already in progress, ignoring duplicate call');
            return;
        }
        this._gameStartInProgress = true;

        console.log('[Main] continueGameStart called', { hasFadeOverlay: !!fadeOverlay });

        // Initialize audio immediately - Drive button click IS the user gesture
        this.sfxManager.initAudioContext();

        // Load UI sounds
        this.sfxManager.loadSound('pip', './assets/sfx/pip3.mp3');
        this.sfxManager.loadSound('upgrade', './assets/sfx/upgrade.mp3');
        this.sfxManager.loadSound('levelAvail', './assets/sfx/levelAvail.mp3');
        this.sfxManager.loadSound('slowdown', './assets/sfx/slowdown.mp3');
        this.sfxManager.loadSound('wavePickup', './assets/sfx/wavePickup.mp3');

        // Load and start engine/rev sounds
        this.engineSound.load().then(() => {
            this.engineSoundReady = true;
            this.sfxManager.startEngineSound();
        });
        this.revSound.load().then(() => {
            this.revSoundReady = true;
        });

        // Fade in audio along with visual fade (2 second fade, starting when visual fade starts)
        this.sfxManager.fadeIn(2000, TIMING.FADE_START_DELAY);

        // Stop menu loop permanently when entering the game
        // Loop only plays on first load; once you enter the game, it never plays again
        if (this.menuLoopPlayer) {
            this.menuLoopPlayer.stop(true); // Mark as superseded - won't play on return to menu
        }

        // Auto-play music with fade-in (respects saved volume, skip if volume is 0)
        const musicVolume = this.gameState.getMusicVolume();
        if (musicVolume > 0 && this.youtubePlayer) {
            setTimeout(() => {
                this.youtubePlayer.playWithFadeIn(musicVolume, 2000);
                // Update music button states to show stop icon
                const mainMenuBtn = document.getElementById('music-toggle-button');
                const gameBtn = document.getElementById('game-music-toggle');
                if (mainMenuBtn) mainMenuBtn.classList.add('playing');
                if (gameBtn) gameBtn.classList.add('playing');
            }, TIMING.FADE_START_DELAY);
        }

        // Enable object spawning first (while screen is still black) - using constants
        setTimeout(() => {
            this.readyToSpawnObjects = true;
            console.log('[Main] Object spawning enabled');
        }, TIMING.SPAWN_ENABLE_DELAY);

        // Then start fade-out after objects have had time to spawn
        setTimeout(() => {
            if (fadeOverlay) {
                // Clear inline opacity so CSS transition works
                fadeOverlay.style.opacity = '';
                fadeOverlay.classList.add('fade-out');
                console.log('[Main] Fade-out started');
            } else {
                console.error('[Main] fadeOverlay is null - cannot start fade');
            }
        }, TIMING.FADE_START_DELAY);

        // Firefox aggressively suspends AudioContext - keep it alive on ANY user interaction
        const keepAudioAlive = () => {
            this.sfxManager?.ensureReady();
        };
        document.addEventListener('click', keepAudioAlive, true);
        document.addEventListener('touchstart', keepAudioAlive, true);

        // Start debug logging for long-running diagnostics
        this.startDebugLogging();

        // Create debug panel for world translation monitoring
        this.debugPanel.setDependencies({
            worldTranslator: this.worldTranslator,
            biomeManager: this.biomeManager,
            gameState: this.gameState,
            poolManager: this.poolManager
        });
        this.debugPanel.create();
    }

    updateStarFieldVisibility() {
        // Delegate to star field manager
        if (this.starFieldManager) {
            const ambientLightIntensity = this.colorCycleManager ? this.colorCycleManager.ambientLightIntensity : 0.6;
            this.starFieldManager.updateVisibility(ambientLightIntensity);
        }
    }

    updateSunPosition() {
        if (!this.gameState) return;

        const gameTimeNorm = this.gameState.getGameTimeNormalized();

        // Calculate sun arc progress (0 = sunrise, 0.5 = noon, 1 = sunset)
        // Sun is above horizon from 6 AM (0.25) to 6 PM (0.75)
        const SUNRISE = 0.25;
        const SUNSET = 0.75;
        let sunArcProgress;

        if (gameTimeNorm >= SUNRISE && gameTimeNorm <= SUNSET) {
            // Sun is above horizon
            sunArcProgress = (gameTimeNorm - SUNRISE) / (SUNSET - SUNRISE);
        } else {
            // Sun is below horizon - clamp to edges
            sunArcProgress = gameTimeNorm > SUNSET ? 1.0 : 0.0;
        }

        // Update visual sun sphere
        if (this.sunManager) {
            const ambientLightIntensity = this.colorCycleManager ? this.colorCycleManager.ambientLightIntensity : 0.6;
            this.sunManager.updatePosition(gameTimeNorm, ambientLightIntensity);

            // Sync fog to sun for atmospheric effect
            if (this.scene?.fog) {
                this.sunManager.updateFog(this.scene.fog.color, this.fogNear, this.fogFar);
            }
        }

        // Update directional light to match sun position (for shadows)
        if (this.lightingManager) {
            this.lightingManager.updateSunPosition(this.camera.position, sunArcProgress);
        }
    }

    updateSunColor() {
        // Delegate to sun manager
        if (this.sunManager && this.colorCycleManager) {
            this.sunManager.setColor(this.colorCycleManager.sunColor);
        }
    }

    updateClouds() {
        // Delegate to cloud manager
        if (this.cloudManager) {
            // Update opacity from color cycle manager
            const opacity = this.colorCycleManager ? this.colorCycleManager.interpolatedCloudOpacity : 0.8;
            this.cloudManager.setOpacity(opacity);
            this.cloudManager.update();
        }
    }

    // NOTE: Bird entity code has been moved to src/entities/Bird.js (BirdManager)
    // NOTE: Plane entity code has been moved to src/entities/Plane.js (PlaneManager)
    // NOTE: Car loading and physics moved to src/entities/CarManager.js

    async loadItemsData() {
        try {
            // Load both old items.json (for colors/milestones) and new generation config
            const [itemsResponse, genConfigResponse] = await Promise.all([
                fetch('data/items.json'),
                fetch('data/item-generation-config.json')
            ]);

            this.itemsData = await itemsResponse.json();
            const itemGenConfig = await genConfigResponse.json();

            // Initialize item generator
            this.itemGenerator = new ItemGenerator(itemGenConfig);

            // Initialize pack generator with item generator
            this.packGenerator = new PackGenerator(this.itemsData, this.gameState, this.itemGenerator);

            // Initialize wave spawn system (handles dynamic spawn timing and quality)
            this.waveSpawnSystem = new WaveSpawnSystem(this.gameState, this.itemsData);
            this.waveSpawnSystem.initializeWaveState();

            // Initialize debug menu (type "iddqd" to open)
            this.debugMenu = new DebugMenu(this.gameState, this.itemGenerator, this.itemsData);
            this.debugMenu.setWavePickupManager(this.wavePickupManager);
            this.debugMenu.setGameUI(this.gameUI);

            // Initialize stat calculator (single source of truth for all stat calculations)
            this.statCalculator = new StatCalculator(this.gameState);
            this.gameState.statCalculator = this.statCalculator;

            console.log('Items system loaded:', itemGenConfig.baseItems.length, 'base items');

            // Update equipped items if equipment manager exists (on game load)
            if (this.equipmentManager) {
                this.equipmentManager.setItemsData(this.itemsData);
                this.equipmentManager.updateEquippedItems();
            }
        } catch (error) {
            console.error('Error loading items data:', error);
        }
    }


    checkPackUnlock() {
        if (!this.waveSpawnSystem || !this.wavePickupManager) return;

        const currentMiles = this.gameState.getTotalMiles();
        const waves = this.gameState.state.waves;
        const nextWaveTarget = waves.nextWaveAtMile || waves.nextMilestone || 25;

        // Spawn distance: spawn pickup when within ~10 miles of target
        // This gives time for it to appear on the road ahead
        const spawnDistance = 10;

        // Check if we should spawn a pickup for the next wave
        // Spawn when within spawnDistance miles of target (so it appears ahead on the road)
        if (currentMiles >= nextWaveTarget - spawnDistance &&
            !this.wavePickupManager.hasPickupAtMilestone(nextWaveTarget)) {

            // Get pack data from wave spawn system (includes pickupType)
            const packData = this.waveSpawnSystem.getCurrentPackData();
            if (packData) {
                // Spawn the pickup on the road with appropriate type
                // NOTE: Don't advance wave target here - do it when collected
                // This protects against quitting before collecting the wave
                this.wavePickupManager.spawnPickup(packData, packData.pickupType);
            }
        }
    }

    /**
     * Handle wave pickup collection - called by WavePickupManager
     */
    handleWavePickupCollected(packData) {
        // Add pack to available packs
        this.gameState.addAvailablePack(packData);

        // Roll next wave spawn point NOW (not at spawn time)
        // This protects against quitting before collecting the wave
        if (this.waveSpawnSystem) {
            const currentMiles = this.gameState.getTotalMiles();
            const result = this.waveSpawnSystem.rollNextWave(currentMiles);
            console.log(`Next wave: ${result.distance} miles ahead (quality: ${result.quality.toFixed(2)})`);
        }

        // Show "New Wave!" event notification
        this.gameUI?.popups.showWaveEvent();

        // Show tutorial modal if this is the first wave
        if (packData.isTutorial && this.gameUI) {
            // Queue the tutorial to show after any popups settle
            setTimeout(() => {
                this.gameUI.showWaveTutorialModal();
            }, 500);
        }

        console.log(`Wave collected! Pack quality: ${packData.packQuality}`);
    }

    /**
     * Handle auto-collect pickup collection (salvage crates, etc.)
     * @param {string} pickupType - Type ID from registry
     * @param {Object} result - Result from onCollect callback
     * @param {Object} packData - Original pack data
     */
    handleAutoCollect(pickupType, result, packData) {
        // Roll next spawn point NOW (same as wave collection)
        // This protects against quitting before collecting
        if (this.waveSpawnSystem) {
            const currentMiles = this.gameState.getTotalMiles();
            const nextResult = this.waveSpawnSystem.rollNextWave(currentMiles);
            console.log(`Next pickup: ${nextResult.distance} miles ahead (type: ${nextResult.pickupType})`);
        }

        // Show type-specific popup
        if (pickupType === 'salvage-crate' && result) {
            this.gameUI?.popups.showSalvageCrateCollected(result.wavedust, result.bonusPercent);
            console.log(`Salvage crate collected! Wavedust: ${result.wavedust}`);
        }
    }

    // SKY COLORS

    updateSkySphereColors() {
        // Delegate to sky manager
        if (this.skyManager && this.colorCycleManager) {
            this.skyManager.setColors(this.colorCycleManager.skyColorTop, this.colorCycleManager.skyColorBottom);
        }
    }

    updateSkySpherePosition() {
        // Delegate to sky manager
        if (this.skyManager) {
            this.skyManager.update();
        }

        // Delegate to star field manager
        if (this.starFieldManager) {
            this.starFieldManager.update();
        }
    }

    updateUI() {
        if (this.gameUI) {
            this.gameUI.update();
        }
    }

    toggleFullscreen() {
        if (!document.fullscreenElement) {
            // Enter fullscreen
            document.documentElement.requestFullscreen().catch(err => {
            });
        } else {
            // Exit fullscreen
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    }

    /**
     * Check if there's a saved offline simulator state on startup
     * Called to recover from tab kill during offline simulation
     * @returns {boolean} True if offline simulator should be resumed
     */
    checkOfflineSimulatorState() {
        const slotKey = this.gameState?.saveManager?.getActiveSaveKey() || 'roadTripSave_anonymous';
        const simulatorState = localStorage.getItem(`offlineSimulatorState_${slotKey}`);
        if (simulatorState) {
            console.log('[Startup] Found saved offline simulator state for slot:', slotKey);
            return true;
        }
        return false;
    }

    /**
     * Initialize Firebase cloud services (if configured)
     * Creates AccountManager, CloudSaveManager, LeaderboardManager, FriendManager
     */
    _initFirebase() {
        if (!firebaseConfigured) {
            console.log('[Firebase] Not configured - cloud features disabled');
            return;
        }

        console.log('[Firebase] Initializing cloud services...');

        // Create account manager (handles auth)
        this.accountManager = new AccountManager();

        // Create friend manager (needs account manager)
        this.friendManager = new FriendManager(this.accountManager);

        // Create leaderboard manager (needs account manager and friend manager)
        this.leaderboardManager = new LeaderboardManager(this.accountManager, this.friendManager);

        // Create cloud save manager (needs account manager)
        this.cloudSaveManager = new CloudSaveManager(this.accountManager);

        // Create save slot service (manages anonymous vs account save slots)
        this.saveSlotService = new SaveSlotService(
            this.gameState.saveManager,
            this.accountManager
        );

        // Create SyncCoordinator (central orchestrator for all save/sync/session operations)
        this.syncCoordinator = new SyncCoordinator({
            saveManager: this.gameState.saveManager,
            cloudSaveManager: this.cloudSaveManager,
            accountManager: this.accountManager,
            gameState: this.gameState
        });

        // Listen for sync status changes
        this.cloudSaveManager.onSyncStatusChanged((status) => {
            if (status.status === 'synced') {
                console.log('[Firebase] Cloud sync complete');
            } else if (status.status === 'error') {
                console.warn('[Firebase] Cloud sync error:', status.error);
            }
        });

        // Listen for auth state changes to handle slot switching
        // NOTE: Initial slot selection is handled by initializeAuthAndSlot() during startup.
        // This handler only processes SUBSEQUENT auth changes (login/logout after initial load).
        this.accountManager.onAuthStateChanged(async (authState) => {
            console.log('[Auth] === AUTH STATE CHANGED ===');
            console.log('[Auth] User:', authState.user?.uid, 'Anonymous:', authState.isAnonymous, 'Linked:', authState.isLinked);

            // Check if we need to switch slots
            const currentSlot = this.gameState.saveManager.getActiveSaveKey();
            const shouldBeAccount = authState.user && !authState.isAnonymous;
            const needsSlotSwitch = shouldBeAccount
                ? currentSlot !== 'roadTripSave_account'
                : currentSlot !== 'roadTripSave_anonymous';

            console.log('[Auth] Current slot:', currentSlot, 'Needs switch:', needsSlotSwitch);

            // Skip if initializeAuthAndSlot() already handled this auth state
            // This prevents double-processing during startup
            if (window._authSlotInitialized && !needsSlotSwitch) {
                console.log('[Auth] Slot already correct (set by initializeAuthAndSlot), skipping');
                return;
            }

            // Skip if startup recovery already handled and slot is correct
            if (window._startupRecoveryComplete && !needsSlotSwitch) {
                console.log('[Auth] Startup recovery handled correct slot, skipping');
                return;
            }

            // Get slot state WITHOUT switching yet (onSlotChanged will handle the switch)
            // We need to load the TARGET slot's state, not switch to it
            const targetSlotKey = shouldBeAccount ? 'roadTripSave_account' : 'roadTripSave_anonymous';
            const targetSlotData = localStorage.getItem(targetSlotKey);
            let slotState = null;
            if (targetSlotData) {
                try {
                    slotState = JSON.parse(targetSlotData);
                } catch (e) {
                    console.warn('[Auth] Failed to parse slot data:', e);
                }
            }

            // Update driver display name
            this.saveSlotService._updateDriverDisplayName(authState);

            // ANONYMOUS USER (or not linked)
            if (!authState.user || authState.isAnonymous) {
                // Get or create anonymous state
                const anonState = slotState || this.gameState.createInitialState();

                // Switch to anonymous slot (onSlotChanged handles the actual switch)
                this.onSlotChanged('anonymous', anonState);
                return;
            }

            // LINKED ACCOUNT USER
            const userId = authState.user.uid;
            console.log('[Auth] Processing linked user:', userId);

            // Skip if FirebaseUI is handling the login flow
            if (window._firebaseUIHandlingLogin) {
                console.log('[Auth] Skipping - FirebaseUI is handling login');
                return;
            }

            // Handle START FRESH flag (user chose not to transfer anonymous progress)
            if (localStorage.getItem('idlemile_start_fresh') === 'true') {
                console.log('[Auth] START FRESH flag - creating fresh account state');
                localStorage.removeItem('idlemile_start_fresh');

                const freshState = this.gameState.createInitialState();
                freshState._cloudSaveOwnerId = userId;

                // onSlotChanged will handle the actual switch and save
                this.onSlotChanged('account', freshState);

                // Sync to cloud after switch
                if (this.cloudSaveManager) {
                    await this.cloudSaveManager.forceLocalToCloud(freshState);
                }
                return;
            }

            // Handle BACKUP LOADED flag (user just loaded from manual backup)
            if (localStorage.getItem('idlemile_backup_loaded') === 'true') {
                console.log('[Auth] BACKUP LOADED flag - restoring from pending backup');
                localStorage.removeItem('idlemile_backup_loaded');

                // Read from the dedicated pending backup key (won't be overwritten by auto-save)
                const pendingBackup = localStorage.getItem('idlemile_pending_backup_restore');
                localStorage.removeItem('idlemile_pending_backup_restore');

                if (pendingBackup) {
                    try {
                        const backupState = JSON.parse(pendingBackup);
                        console.log('[Auth] Restoring backup state:', {
                            miles: backupState?.miles,
                            totalMiles: backupState?.totalMiles,
                            lifetimeMiles: backupState?.lifetimeMiles,
                            fuel: backupState?.fuel,
                            momentum: backupState?.session?.exitMomentum,
                            prestigeLevel: backupState?.prestige?.level,
                            wavesNextMile: backupState?.waves?.nextWaveAtMile,
                            starterSpeed: backupState?.cars?.starter?.upgradeLevels?.baseSpeed
                        });

                        backupState._cloudSaveOwnerId = userId;

                        // Clear the simulator state AGAIN (startup may have read it before we could clear it)
                        localStorage.removeItem('offlineSimulatorState_roadTripSave_account');

                        // Save to the account slot FIRST
                        localStorage.setItem('roadTripSave_account', JSON.stringify(backupState));

                        // Stop any running simulator
                        if (this.offlineSimulator?.isRunning) {
                            this.offlineSimulator.stop();
                        }

                        // Then do the slot change with the backup state
                        this.onSlotChanged('account', backupState);
                        this.gameState.save(true); // Force save to ensure persistence

                        // Reset the offline display to show backup's fuel/momentum
                        if (this.offlineDisplayManager) {
                            this.offlineDisplayManager.resetToSlot();
                        }
                    } catch (e) {
                        console.error('[Auth] Failed to parse pending backup:', e);
                    }
                }
                return;
            }

            // Normal account login
            // First switch to account slot (this stops old simulator, applies progress)
            let accountState = slotState || this.gameState.createInitialState();
            accountState._cloudSaveOwnerId = userId;

            // Switch slot FIRST (stops old simulator, saves old progress)
            this.onSlotChanged('account', accountState);

            // Sync with cloud
            try {
                const result = await this.cloudSaveManager.syncWithCloud(accountState);
                console.log('[Auth] Cloud sync result:', result);

                if (result === 'conflict') {
                    // Page refresh with logged-in user - use local state, sync to cloud
                    this.cloudSaveManager.forceLocalToCloud(accountState);
                    this.gameState.save();
                } else if (result === 'cloud' || result === 'none') {
                    const cloudState = await this.cloudSaveManager.loadFromCloud();
                    if (cloudState) {
                        cloudState._cloudSaveOwnerId = userId;
                        // Update state and save (now to correct slot)
                        this.gameState.state = cloudState;
                        this.gameState.save();
                        this.sfxManager?.refreshVolumeFromState();
                        // NOTE: Do NOT call resetToSlot() or restart simulator here.
                        // onSlotChanged already resumed the simulator with batch-calculated
                        // offline progress. The simulator is running and handling the display.
                        console.log('[Auth] Applied cloud state:', cloudState.totalMiles, 'miles, fuel:', cloudState.fuel);
                    } else if (result === 'none') {
                        // New user, state already set by onSlotChanged
                        this.gameState.save();
                    }
                } else if (result === 'local') {
                    this.gameState.save();
                }
            } catch (err) {
                console.warn('[Auth] Cloud sync failed:', err);
            }
        });

        console.log('[Firebase] Cloud services initialized');

        // Initialize save slots (migrates legacy save only)
        // NOTE: Do NOT set this.gameState.state here! The auth handler's onSlotChanged
        // already set the correct state. Setting it here causes a race condition where
        // we overwrite the state AFTER the simulator has already added waves to it.
        this.saveSlotService.initialize().then(() => {
            console.log('[Firebase] SaveSlotService initialized (migration only)');
        }).catch(err => {
            console.warn('[Firebase] SaveSlotService initialization failed:', err);
        });

        // Initialize SyncCoordinator (sets up its own session invalidation callback)
        // Note: This will override the inline session invalidation handler above,
        // which is intentional - SyncCoordinator handles kicks and emits events for main.js
        this.syncCoordinator.initialize().then(() => {
            console.log('[Firebase] SyncCoordinator initialized');
        }).catch(err => {
            console.warn('[Firebase] SyncCoordinator initialization failed:', err);
        });

    }

    /**
     * Close all open modals and overlays
     * Called before entering offline simulation to ensure clean state on resume
     */
    closeAllModals() {
        console.log('[Main] Closing all modals');

        // Close UpgradeMenuManager if open
        if (this.gameUI?.upgradeMenu?.forceClose()) {
            console.log('[Main] Closed upgrade menu');
        }

        // Close all ModalManager modals
        if (this.gameUI?.modals?.closeAll()) {
            console.log('[Main] Closed ModalManager modals');
        }

        // Also clear modal queue to prevent queued modals from appearing
        if (this.gameUI?.modals) {
            this.gameUI.modals.modalQueue = [];
        }

        // Remove any stray modal overlays that might exist
        // (defensive cleanup for modals created outside ModalManager)
        document.querySelectorAll('.gameui-modal-overlay, .cf-modal-overlay, .gameui-fullscreen-overlay').forEach(el => {
            console.log('[Main] Removing stray modal overlay:', el.className);
            el.remove();
        });
    }

    /**
     * Unload the game and enter offline simulation mode
     * Called when: mobile tab is backgrounded, or desktop user presses ESC and confirms
     */
    unloadGame() {
        if (!this.gameStarted) return;

        console.log('[Main] Unloading game, entering offline simulation mode');

        // 0. Close all open modals first (before stopping game loop)
        this.closeAllModals();

        console.log('[Main] Step 1: Stopping game loop');

        // 1. Stop the game loop
        this.gameStarted = false;
        this._gameStartInProgress = false; // Reset guard for next start
        this.stopFallbackTimer();

        // Stop active gameplay cloud sync (will switch to offline sync)
        this._stopActiveGameplaySync();

        // 2. Save session state with current momentum
        const currentMomentum = this.driveStateMachine?.momentumBonus || 0;
        this.gameState.saveSessionState(currentMomentum);

        // 2b. Force sync full game state to cloud immediately (fire-and-forget)
        // This ensures active gameplay progress is saved before entering simulator
        if (this.cloudSaveManager && this.accountManager?.isLinked()) {
            this.cloudSaveManager.forceLocalToCloud(this.gameState.state).catch(err => {
                console.warn('[Main] Cloud sync on unload failed:', err);
            });
        }

        // 3. Stop SFX audio (temporarily, don't persist mute state)
        // Note: YouTube music continues playing across offline mode
        // Stop engine sound and silence master gain (so fade-in works on return)
        if (this.sfxManager?.engineSound?.isPlaying) {
            this.sfxManager.engineSound.stop();
        }
        this.sfxManager?.silence();

        // 4. Hide canvas (don't dispose renderer - we'll reuse it)
        if (this.renderer?.domElement) {
            this.renderer.domElement.style.display = 'none';
        }

        // 5. Hide game UI
        const gameUIContainer = document.getElementById('game-ui');
        if (gameUIContainer) {
            gameUIContainer.style.display = 'none';
        }

        // 6. Show main menu with offline display
        this._showMainMenuWithOfflineMode();

        // 7. Start offline simulator
        this._startOfflineSimulator(currentMomentum);

        // 8. Dispose old scene (preload will happen when user clicks DRIVE)
        // This ensures chunks are created with correct miles after simulation ends
        this.disposeScene();
    }

    /**
     * Show main menu in offline simulation mode
     */
    _showMainMenuWithOfflineMode() {
        const startScreen = document.getElementById('start-screen');
        const offlineDisplay = document.getElementById('offline-progress-display');

        // Show start screen immediately (no fade)
        if (startScreen) {
            startScreen.classList.remove('hidden');
            startScreen.style.opacity = '1';
            startScreen.style.transition = 'none';
        }

        // Set button to ready state immediately (preload happens on click)
        const startButton = document.getElementById('start-button');
        const startButtonText = document.getElementById('start-button-text');
        const startButtonProgress = document.getElementById('start-button-progress');
        if (startButton) {
            if (startButtonText) startButtonText.textContent = 'DRIVE';
            if (startButtonProgress) startButtonProgress.style.width = '100%';
            startButton.classList.add('ready');
        }

        // Show secondary buttons container during offline mode
        const secondaryButtons = document.getElementById('secondary-buttons');
        if (secondaryButtons) {
            secondaryButtons.classList.add('ready');
        }

        // Keep NEW GAME button visible during offline mode
        const newGameButton = document.getElementById('new-game-button');
        if (newGameButton) {
            newGameButton.classList.add('ready');
        }

        // Show offline progress display
        if (offlineDisplay) {
            offlineDisplay.classList.add('active');
        }

        // Set up speed breakdown tooltip
        this.offlineDisplayManager.setupSpeedTooltip();

        // Set up prestige badge (if applicable)
        this.offlineDisplayManager.setupPrestigeBadge();
    }

    /**
     * Dispose the entire 3D scene and all managers
     * Called when entering offline simulation for a clean slate
     */
    disposeScene() {
        // Dispose equipment manager (not registered with preloader)
        this.equipmentManager?.dispose();
        this.equipmentManager = null;

        // Use preloader to dispose all registered managers in reverse order
        if (this.scenePreloader) {
            this.scenePreloader.disposeAll();
            this.scenePreloader = null;
        }

        // Clear manager references (dispose was called via preloader)
        this.carManager = null;
        this.signManager = null;
        this.wavePickupManager = null;
        this.planeManager = null;
        this.birdManager = null;
        this.cameraController = null;
        this.biomeManager = null;
        this.poolManager = null;
        this.cloudManager = null;
        this.sunManager = null;
        this.starFieldManager = null;
        this.skyManager = null;
        this.lightingManager = null;

        // Dispose world translator (not registered with preloader)
        this.worldTranslator?.dispose();
        this.worldTranslator = null;

        // Dispose audio
        if (this.engineSound) {
            this.engineSound.dispose?.();
            this.engineSound = null;
            this.engineSoundReady = false;
        }

        if (this.revSound) {
            this.revSound.dispose?.();
            this.revSound = null;
            this.revSoundReady = false;
        }

        // Clear audio listener from camera
        if (this.audioListener && this.camera) {
            this.camera.remove(this.audioListener);
            this.audioListener = null;
        }

        // Remove camera from scene
        if (this.camera && this.scene) {
            this.scene.remove(this.camera);
            this.camera = null;
        }

        // Clear scene fog
        if (this.scene) {
            this.scene.fog = null;
        }

        // Clear remaining scene children (should be empty, but safety check)
        if (this.scene) {
            while (this.scene.children.length > 0) {
                this.scene.remove(this.scene.children[0]);
            }
            this.scene = null;
        }

        // Clear renderer caches to prevent memory accumulation
        if (this.renderer) {
            this.renderer.renderLists.dispose();
        }

        // Clear global references
        window.worldTranslator = null;
        window.startGame = null;
    }

    /**
     * Update the loading progress bar during preload
     */
    _updatePreloadProgress(percent) {
        const startButtonProgress = document.getElementById('start-button-progress');
        if (startButtonProgress) {
            startButtonProgress.style.width = percent + '%';
        }
    }

    /**
     * Preload a fresh scene in the background
     * Called after disposeScene() while simulator is running
     * @returns {Promise} Resolves when scene is ready
     */
    async preloadScene() {
        console.log('[Main] Preloading fresh scene...');
        this._scenePreloading = true;
        this._sceneReady = false;

        // Create preloader - managers self-register when passed this instance
        // Store on this so disposeScene can use it later
        this.scenePreloader = new ScenePreloader((percent) => {
            this._updatePreloadProgress(percent);
        });
        const preloader = this.scenePreloader;

        // Recreate scene, camera, audio (no manager, so register manually)
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x000000);
        this.scene.fog = new THREE.Fog(this.colorCycleManager.fogColor, this.fogNear, this.fogFar);

        this.camera = new THREE.PerspectiveCamera(
            CAMERA.FOV,
            window.innerWidth / window.innerHeight,
            CAMERA.NEAR_PLANE,
            CAMERA.FAR_PLANE
        );
        this.camera.position.set(
            CAMERA.INITIAL_POSITION.x,
            CAMERA.INITIAL_POSITION.y,
            CAMERA.INITIAL_POSITION.z
        );
        this.camera.lookAt(
            CAMERA.INITIAL_LOOK_AT.x,
            CAMERA.INITIAL_LOOK_AT.y,
            CAMERA.INITIAL_LOOK_AT.z
        );
        this.scene.add(this.camera);

        this.audioListener = new THREE.AudioListener();
        this.camera.add(this.audioListener);
        preloader.complete('scene');

        // Reinitialize lighting (self-registers via preloader)
        this.lightingManager = new LightingManager(this.scene, this.camera, preloader);
        this.lightingManager.setAmbient(this.colorCycleManager.ambientLightColor, this.colorCycleManager.ambientLightIntensity);
        this.lightingManager.setDirectional(this.colorCycleManager.directionalLightColor, this.colorCycleManager.directionalLightIntensity);
        this.lightingManager.initialize();

        // Reinitialize sky
        this.skyManager = new SkyManager(this.scene, this.camera, preloader);
        this.skyManager.setColors(this.colorCycleManager.skyColorTop, this.colorCycleManager.skyColorBottom);
        this.skyManager.initialize();

        // Reinitialize star field
        this.starFieldManager = new StarFieldManager(this.scene, this.camera, preloader);
        this.starFieldManager.initialize();

        // Reinitialize sun
        this.sunManager = new SunManager(this.scene, this.camera, preloader);
        this.sunManager.setColor(this.colorCycleManager.sunColor);
        this.sunManager.initialize();

        // Reinitialize world translator
        this.worldTranslator = new WorldTranslator(this.scene);
        window.worldTranslator = this.worldTranslator;

        // Reinitialize cloud manager
        this.cloudManager = new CloudManager(this.scene, this.camera, this.worldTranslator, {
            fogFar: this.fogFar,
            isMobile: this.isMobile
        }, preloader);
        this.cloudManager.setColorPalettes(COLOR_PALETTES);
        this.cloudManager.setPaletteIndex(this.colorCycleManager.getCurrentPaletteIndex());
        this.cloudManager.initialize();

        // Reinitialize pool manager and biome
        this.poolManager = new PoolManager(preloader);
        await this.poolManager.initialize('data/roadside-objects.json');

        this.biomeManager = new BiomeManager(this.scene, this.camera, this.worldTranslator, {
            chunkCount: this.chunkCount
        }, preloader);
        this.biomeManager.setPoolManager(this.poolManager);

        const startingMiles = this.gameState.getTotalMiles();
        await this.biomeManager.initialize(startingMiles);

        // Update color cycle manager dependencies
        this.colorCycleManager.setDependencies({
            biomeManager: this.biomeManager,
            cloudManager: this.cloudManager,
            lightingManager: this.lightingManager,
            skyManager: this.skyManager,
            sunManager: this.sunManager,
            scene: this.scene
        });

        // Set reverse reference so BiomeManager can notify ColorCycleManager on biome change
        this.biomeManager.setColorCycleManager(this.colorCycleManager);

        // Set initial palette
        const currentPaletteIndex = this.colorCycleManager.getCurrentPaletteIndex();
        this.biomeManager.setPalette(COLOR_PALETTES[currentPaletteIndex], currentPaletteIndex);

        // Reinitialize camera controller (no preloader support yet)
        this.cameraController = new CameraController(this.camera, this.scene, {
            height: CAMERA.HEIGHT,
            lookAtDistance: CAMERA.LOOK_AT_DISTANCE,
            yawSmoothing: CAMERA.YAW_SMOOTHING,
            pitchSmoothing: CAMERA.PITCH_SMOOTHING,
            yawOffset: CAMERA.YAW_OFFSET,
            maxPitch: CAMERA.MAX_PITCH,
            lateralOffset: CAMERA.LATERAL_OFFSET
        });
        this.cameraController.setDependencies({
            biomeManager: this.biomeManager,
            worldTranslator: this.worldTranslator,
            lightingManager: this.lightingManager
        });
        this.cameraController.initializeFromRoad();
        preloader.complete('cameraController');

        // Reset travel distance tracking to sync with new cameraController
        this.travelDistance = 0;
        this.distance = 0;
        this.gameState.distanceAtLastMileUpdate = 0;

        // Reinitialize bird manager
        this.birdManager = new BirdManager(this.scene, this.camera, this.worldTranslator, {
            enabled: BIRDS.ENABLED,
            count: BIRDS.COUNT,
            size: BIRDS.SIZE,
            minHeight: BIRDS.MIN_HEIGHT,
            maxHeight: BIRDS.MAX_HEIGHT,
            minSpeed: BIRDS.MIN_SPEED,
            maxSpeed: BIRDS.MAX_SPEED,
            fadeThreshold: BIRDS.FADE_THRESHOLD,
            fogFar: this.fogFar
        }, preloader);
        this.birdManager.initialize();

        // Reinitialize plane manager
        this.planeManager = new PlaneManager(this.scene, this.camera, this.worldTranslator, {
            enabled: PLANES.ENABLED,
            size: PLANES.SIZE,
            minHeight: PLANES.MIN_HEIGHT,
            maxHeight: PLANES.MAX_HEIGHT,
            minSpawnDistance: PLANES.MIN_SPAWN_DISTANCE,
            maxSpawnDistance: PLANES.MAX_SPAWN_DISTANCE,
            speed: PLANES.SPEED,
            minSpawnInterval: PLANES.MIN_SPAWN_INTERVAL,
            maxSpawnInterval: PLANES.MAX_SPAWN_INTERVAL,
            maxCount: PLANES.MAX_COUNT
        }, preloader);
        this.planeManager.initialize();

        // Reinitialize wave pickup manager
        this.wavePickupManager = new WavePickupManager(
            this.scene,
            this.camera,
            this.biomeManager,
            this.gameState,
            this.worldTranslator,
            this.sfxManager,
            preloader
        );
        await this.wavePickupManager.initialize();
        this.wavePickupManager.onPickupCollected = (packData) => {
            this.handleWavePickupCollected(packData);
        };
        this.wavePickupManager.onAutoCollect = (pickupType, result, packData) => {
            this.handleAutoCollect(pickupType, result, packData);
        };

        // Reinitialize sign manager
        this.signManager = new SignManager(
            this.scene,
            this.camera,
            this.biomeManager,
            this.worldTranslator,
            this.gameState,
            {},
            preloader
        );
        this.signManager.setLightingManager(this.lightingManager);
        this.signManager.registerSignType(WAVE_SIGN_TYPE);
        this.signManager.setDefaultSignType('highway');
        await this.signManager.initialize();

        // Reinitialize car manager
        this.carManager = new CarManager(this.camera, {
            basePosition: CAR.BASE_POSITION
        }, preloader);
        this.carManager.setDependencies({
            biomeManager: this.biomeManager,
            worldTranslator: this.worldTranslator
        });
        this.carManager.onCarLoaded = (car) => {
            this.equipmentManager = new EquipmentManager(this.gameState, this.itemsData, car);
            this.equipmentManager.slotPositions = {
                dashboard: this.dashboardSlotPosition,
                keychain: this.keychainSlotPosition,
                rearview: this.rearviewSlotPosition
            };
            this.equipmentManager.itemScale = this.itemPrimitiveScale;
            this.equipmentManager.createSlots();
        };
        await this.carManager.loadCar();

        // Recreate engine and rev sounds (use SFXManager's audio context)
        this.engineSound = new EngineSound(this.sfxManager);
        this.revSound = new RevSound(this.sfxManager);
        this.engineSoundReady = false;
        this.revSoundReady = false;

        // Load sounds (context should already be initialized from previous session)
        this.engineSound.load().then(() => {
            this.engineSoundReady = true;
        });
        this.revSound.load().then(() => {
            this.revSoundReady = true;
        });

        // Register sounds with SFXManager
        this.sfxManager.setEngineSound(this.engineSound);
        this.sfxManager.setRevSound(this.revSound);
        preloader.complete('sounds');

        // Set up startGame reference
        window.startGame = () => {
            this.startFadeInAndMusic();
        };

        this._scenePreloading = false;
        this._sceneReady = true;

        // Update button to ready state
        const startButton = document.getElementById('start-button');
        const startButtonText = document.getElementById('start-button-text');
        if (startButton) {
            if (startButtonText) startButtonText.textContent = 'DRIVE';
            startButton.classList.add('ready');
        }

        // Ensure secondary buttons container is visible
        const secondaryButtons = document.getElementById('secondary-buttons');
        if (secondaryButtons) {
            secondaryButtons.classList.add('ready');
        }

        console.log('[Main] Scene preloaded and ready');
    }

    /**
     * Start the offline simulator
     */
    _startOfflineSimulator(initialMomentum) {
        if (!this.offlineSimulator) {
            this.offlineSimulator = new OfflineSimulator(this.gameState);
        }

        // Wire up offlineDisplayManager with simulator reference
        this.offlineDisplayManager.offlineSimulator = this.offlineSimulator;

        // Set up UI update callback
        this.offlineSimulator.onUpdate = (progress) => {
            this.offlineDisplayManager.updateDisplay(progress);
        };

        this.offlineSimulator.onFuelDepleted = () => {
            this.offlineDisplayManager.handleFuelDepleted();
        };

        this.offlineSimulator.start(initialMomentum);
        console.log('[Simulator] Started for slot:', this.gameState.saveManager.getActiveSaveKey());
    }

    /**
     * Handle save slot change (login/logout)
     * Suspends current simulator (preserving progress), switches slot, resumes/starts for new slot
     * @param {string} newSlot - 'anonymous' or 'account'
     * @param {object} newSlotState - The game state for the new slot (already loaded)
     */
    onSlotChanged(newSlot, newSlotState) {
        console.log('[Simulator] === SLOT CHANGE ===');
        console.log('[Simulator] Switching to:', newSlot);

        const oldSlot = this.gameState.saveManager.getActiveSaveKey();
        const leavingAccount = oldSlot === 'roadTripSave_account';

        // 1. Suspend current simulator (preserves state for later resume)
        if (this.offlineSimulator?.isRunning || this.offlineSimulator?.isSuspended) {
            this.offlineSimulator.suspend();
            console.log('[Simulator] Suspended simulator (state saved for resume)');
        }

        // 2. If leaving account, sync current gameState to cloud
        if (leavingAccount && this.cloudSaveManager) {
            this.cloudSaveManager.forceLocalToCloud(this.gameState.state).catch(err => {
                console.warn('[Simulator] Cloud sync on signout failed:', err);
            });
            console.log('[Simulator] Syncing account to cloud on signout');
        }

        // 3. Switch to new slot and record it for future startups
        if (newSlot === 'account') {
            this.gameState.saveManager.switchToAccount();
            this.saveSlotService?.setLastActiveSlot('account');
        } else {
            this.gameState.saveManager.switchToAnonymous();
            this.saveSlotService?.setLastActiveSlot('anonymous');
        }

        // 4. Load new slot's state
        if (newSlotState) {
            this.gameState.state = newSlotState;
            console.log('[Simulator] Loaded new slot state:', {
                miles: newSlotState.totalMiles,
                fuel: newSlotState.fuel
            });

            // Refresh audio settings for new slot
            this.sfxManager?.refreshVolumeFromState();
        }

        // 5. Reset display for new slot
        this.offlineDisplayManager?.resetToSlot();

        // 6. Update driver name display
        this._updateDriverNameDisplay(newSlot);

        // 7. Check for existing simulator state for new slot, or start fresh
        const slotKey = this.gameState.saveManager.getActiveSaveKey();
        const hasSimulatorState = localStorage.getItem(`offlineSimulatorState_${slotKey}`) !== null;

        if (!this.offlineSimulator) {
            this.offlineSimulator = new OfflineSimulator(this.gameState);
        }

        // Wire up callbacks
        this.offlineDisplayManager.offlineSimulator = this.offlineSimulator;
        this.offlineSimulator.onUpdate = (progress) => {
            this.offlineDisplayManager.updateDisplay(progress);
        };
        this.offlineSimulator.onFuelDepleted = () => {
            this.offlineDisplayManager.handleFuelDepleted();
        };

        if (hasSimulatorState) {
            // Resume from saved state
            const resumed = this.offlineSimulator.resume();
            console.log('[Simulator] Resumed for new slot:', resumed);
        } else {
            // Start fresh if there's fuel
            const fuel = this.gameState.getFuel();
            const momentum = this.gameState.state?.session?.exitMomentum || 0;
            if (fuel > 0) {
                this.offlineSimulator.start(momentum);
                console.log('[Simulator] Started fresh for new slot with momentum:', momentum);
            } else {
                console.log('[Simulator] No fuel, not starting simulator');
            }
        }

        // Update display immediately and ensure it's visible
        this.offlineDisplayManager?.setupSpeedTooltip();
        this.offlineDisplayManager?.setupPrestigeBadge();
        if (this.offlineSimulator.isRunning || this.offlineSimulator.isSuspended) {
            // Simulator is running - show live progress
            const offlineDisplay = document.getElementById('offline-progress-display');
            if (offlineDisplay) {
                offlineDisplay.classList.add('active');
            }
            this.offlineDisplayManager?.updateDisplay(this.offlineSimulator.getProgress());
        } else {
            // No simulator running (no fuel) - show depleted state
            // Pass progress so that any accumulated offline progress is displayed
            this.offlineDisplayManager?.showDepletedState(this.offlineSimulator?.getProgress());
        }

        console.log('[Simulator] Slot change complete');
    }

    /**
     * Update the driver name display based on slot
     * @param {string} slot - 'anonymous' or 'account'
     */
    _updateDriverNameDisplay(slot) {
        const el = document.getElementById('opd-driver-name');
        if (!el) return;

        if (slot === 'anonymous') {
            el.textContent = 'ANONYMOUS DRIVER';
            el.classList.remove('linked');
            el.classList.add('anonymous');
        } else {
            // Get display name from account manager
            const user = this.accountManager?.currentUser;
            const name = user?.displayName ||
                        user?.email?.split('@')[0] ||
                        'LINKED DRIVER';
            el.textContent = name.toUpperCase();
            el.classList.add('linked');
            el.classList.remove('anonymous');
        }
    }

    /**
     * Start periodic cloud syncing during active gameplay (every 60s)
     */
    _startActiveGameplaySync() {
        if (this.syncCoordinator) {
            this.syncCoordinator.startActiveGameplaySync();
        }
    }

    /**
     * Stop periodic cloud syncing during active gameplay
     */
    _stopActiveGameplaySync() {
        if (this.syncCoordinator) {
            this.syncCoordinator.stopActiveGameplaySync();
        }
    }

    /**
     * Reload the game after offline simulation
     * Called when user clicks START/DRIVE button
     */
    async reloadGame() {
        console.log('[Main] Reloading game from offline simulation');

        // 0. Close any modals that might still be open (defensive)
        // This handles edge cases where modal state persisted across backgrounding
        this.closeAllModals();

        // 1. Stop offline simulator and get progress
        // Also handle case where fuel depleted (isRunning=false but has progress)
        let progress = this.offlineSimulator?.stop();

        // If stop() returned null but we have pending state (fuel depleted case), get progress directly
        if (!progress && this.offlineSimulator) {
            progress = this.offlineSimulator.getProgress();
            // Clear the localStorage state since we're applying it now
            const slotKey = this.gameState?.saveManager?.getActiveSaveKey() || 'roadTripSave_anonymous';
            localStorage.removeItem(`offlineSimulatorState_${slotKey}`);
        }

        // 2. Hide offline display and prestige badge
        const offlineDisplay = document.getElementById('offline-progress-display');
        if (offlineDisplay) {
            offlineDisplay.classList.remove('active');
        }
        this.offlineDisplayManager?.hidePrestigeBadge();

        // 3. Format and apply progress directly (no modal)
        // This updates gameState.miles BEFORE preloading so chunks are positioned correctly
        if (progress) {
            const formattedProgress = {
                offlineSeconds: progress.elapsedSeconds,
                simulatedSeconds: progress.elapsedSeconds,
                milesDriven: progress.simulatedMiles,
                wavesFound: progress.wavesFound,
                restoredMomentum: progress.currentMomentum,
                cappedByFuel: progress.cappedByFuel,
                newGameTime: this._calculateNewGameTime(progress.elapsedSeconds),
                timeScale: this.gameState.getEffectiveTimeScale()
            };
            this.gameState.applyOfflineProgress(formattedProgress);

            // Check for tutorial wave collected offline
            const waves = this.gameState.state.waves;
            if (waves?.tutorialCollectedOffline && !waves?.tutorialShown) {
                waves.tutorialCollectedOffline = false;
                this.gameState.save();
                this.gameUI.modals.queue(() => {
                    this.gameUI.showWaveTutorialModal();
                    return { close: () => {} };
                });
            }
        }

        // 4. Now preload scene with correct miles (show loading progress in button)
        const startButton = document.getElementById('start-button');
        const startButtonText = document.getElementById('start-button-text');
        const startButtonProgress = document.getElementById('start-button-progress');

        if (startButton) {
            if (startButtonText) startButtonText.textContent = 'LOADING';
            if (startButtonProgress) startButtonProgress.style.width = '0%';
            startButton.classList.remove('ready');
        }

        console.log('[Main] Starting scene preload with final miles:', this.gameState.getTotalMiles());
        await this.preloadScene();
        console.log('[Main] Scene preload complete');

        // 5. Continue with game reload (fade from black)
        this._continueReload();
    }

    /**
     * Continue reload process after offline simulation
     * Does a fresh game start with fade-in from black
     */
    _continueReload() {
        console.log('[Main] _continueReload starting');

        // 1. Reset fade overlay to black INSTANTLY (no transition)
        const fadeOverlay = document.getElementById('fade-overlay');
        if (fadeOverlay) {
            // Ensure fade overlay is visible and solid black
            fadeOverlay.style.display = 'block';
            fadeOverlay.style.transition = 'none';
            fadeOverlay.style.opacity = '1';
            fadeOverlay.classList.remove('fade-out');
            // Force reflow to apply instant change
            fadeOverlay.offsetHeight;
            // Restore transition for fade animation
            fadeOverlay.style.transition = '';
            console.log('[Main] Fade overlay reset to black');
        } else {
            console.error('[Main] Fade overlay not found!');
        }

        // 2. Check WebGL context - may have been lost during long suspension
        if (this.renderer) {
            const gl = this.renderer.getContext();
            if (gl.isContextLost()) {
                console.warn('[Main] WebGL context was lost - attempting recovery');
                // Force context restoration by triggering a resize
                this.renderer.setSize(
                    Math.round(window.innerWidth * RENDERING.RESOLUTION_SCALE),
                    Math.round(window.innerHeight * RENDERING.RESOLUTION_SCALE),
                    false
                );
            }
            // Show canvas
            this.renderer.domElement.style.display = '';
        }

        // 3. Hide main menu immediately (fade overlay covers the transition)
        const startScreen = document.getElementById('start-screen');
        if (startScreen) {
            startScreen.classList.add('hidden');
            startScreen.style.opacity = '1'; // Reset for next time
            startScreen.style.transition = 'none';
        }

        // 4. Show game UI
        const gameUIContainer = document.getElementById('game-ui');
        if (gameUIContainer) {
            gameUIContainer.style.display = '';
        }

        // 5. Update biome manager with current miles (fixes chunk rendering)
        if (this.biomeManager) {
            const currentMiles = this.gameState.getTotalMiles();
            this.biomeManager.updateMiles(currentMiles);
            console.log('[Main] Biome manager updated with miles:', currentMiles);
        }

        // 5b. Recalculate palette for new game time (fixes cloud colors after simulator)
        if (this.colorCycleManager) {
            this.colorCycleManager.calculateStartingPaletteFromGameTime();
            this.colorCycleManager.applyStartingPalette();
            console.log('[Main] Color palette recalculated for game time');
        }

        // Log scene state for debugging
        console.log('[Main] Scene state:', {
            sceneChildren: this.scene?.children?.length,
            cameraPosition: this.camera?.position,
            rendererSize: this.renderer ? {
                width: this.renderer.domElement.width,
                height: this.renderer.domElement.height
            } : null
        });

        // 6. Initialize momentum tracking
        const currentTime = performance.now() / 1000;
        this.driveStateMachine.lastInteractionTime = currentTime;
        this.driveStateMachine.momentumBonus = 0;

        // Restore momentum from session
        // Use setRestoredMomentum() for more reliable cross-browser behavior
        const savedMomentum = this.gameState.getRestoredMomentum();
        if (savedMomentum > 0) {
            this.driveStateMachine.setRestoredMomentum(savedMomentum);
        }

        // 7. Enable game loop
        this.gameStarted = true;
        this.lastTime = performance.now(); // Reset timing to avoid huge delta on first frame

        // Start periodic cloud sync during active gameplay (every 60s)
        this._startActiveGameplaySync();

        // 8. Ensure animation loop is running (may have stopped during long suspension)
        // Stop any fallback timer first to avoid duplicate loops
        if (this.fallbackTimerInterval) {
            clearInterval(this.fallbackTimerInterval);
            this.fallbackTimerInterval = null;
        }
        this.isUsingFallbackTimer = false;
        // Explicitly restart RAF loop
        requestAnimationFrame(() => this.animate());
        console.log('[Main] Animation loop restarted');

        // 9. Start music, sounds, and fade-in
        this.continueGameStart(fadeOverlay);
    }

    /**
     * Calculate new game time after offline simulation
     */
    _calculateNewGameTime(elapsedSeconds) {
        const timeScale = this.gameState.getEffectiveTimeScale();
        const gameSeconds = elapsedSeconds * timeScale;
        const currentGameTime = this.gameState.state.gameTime;
        return (currentGameTime + gameSeconds) % this.gameState.GAME_DAY_LENGTH;
    }

    // NOTE: Offline display methods moved to src/ui/OfflineDisplayManager.js

    // NOTE: Color cycle logic moved to src/rendering/ColorCycleManager.js

    animate() {
        // Pause if game hasn't started - don't schedule another frame
        if (!this.gameStarted) {
            return;
        }

        // Only use requestAnimationFrame if not using fallback timer
        if (!this.isUsingFallbackTimer) {
            requestAnimationFrame(() => this.animate());
        }

        // Calculate delta time
        const currentTime = performance.now();
        const deltaTime = (currentTime - this.lastTime) / 1000; // Convert to seconds
        this.lastTime = currentTime;
        const currentTimeSeconds = currentTime / 1000; // Cache for state machine

        // Update game clock (uses unified timeScale)
        this.gameState.update(deltaTime);

        // Periodically save momentum for restoration on game reload
        const currentMomentum = this.driveStateMachine?.momentumBonus || 0;
        this.gameState.updateMomentum(currentMomentum);

        // Get effective timeScale (base + item bonuses)
        const timeScale = this.gameState.getEffectiveTimeScale();

        // Update drive state machine and get current speed, scaled by timeScale
        // At timeScale=1 (real time): visual speed matches real-world perception
        // At timeScale>1: everything moves faster (visual, miles, day/night)
        this.speed = this.driveStateMachine.update(deltaTime, currentTimeSeconds) * timeScale;
        this.gameState.updateMiles(this.travelDistance);

        // Check for pack unlock
        this.checkPackUnlock();

        // Update camera controller
        if (this.cameraController) {
            this.cameraController.update(deltaTime, this.speed);
            this.travelDistance = this.cameraController.getTravelDistance();
            this.distance = this.travelDistance;
        }

        // Update biome system (handles road, terrain, and roadside objects)
        if (this.biomeManager) {
            this.biomeManager.update(deltaTime);
            this.biomeManager.updateMiles(this.gameState.getTotalMiles());
        }

        // Update color cycle (day/night transitions)
        if (this.colorCycleManager) {
            this.colorCycleManager.update(deltaTime);
        }

        // Update time of day for stat and effect calculators
        const paletteNames = ['day', 'sunset', 'night', 'dawn'];
        const currentPaletteIndex = this.colorCycleManager ? this.colorCycleManager.getCurrentPaletteIndex() : 0;
        const timeOfDay = paletteNames[currentPaletteIndex] || 'day';

        if (this.statCalculator) {
            this.statCalculator.setTimeOfDay(timeOfDay);
        }

        this.updateSkySpherePosition();
        this.updateStarFieldVisibility();
        this.updateSunPosition();
        this.updateClouds();

        // Update bird and plane managers
        const ambientLightIntensity = this.colorCycleManager ? this.colorCycleManager.ambientLightIntensity : 0.6;
        if (this.birdManager) {
            this.birdManager.update(deltaTime, ambientLightIntensity);
        }
        if (this.planeManager) {
            this.planeManager.update(deltaTime, ambientLightIntensity);
        }
        if (this.wavePickupManager) {
            this.wavePickupManager.update(deltaTime);
        }
        if (this.signManager) {
            this.signManager.update(deltaTime);
        }

        if (this.carManager) {
            this.carManager.update(deltaTime, this.speed);
        }

        // Update equipped item animations
        if (this.equipmentManager) {
            this.equipmentManager.update(deltaTime);
        }

        this.sfxManager.updateEngineSound(this.speed);

        // Throttle UI updates for better mobile performance (using constants)
        if (currentTime - this.lastUIUpdateTime > RENDERING.UI_UPDATE_INTERVAL) {
            this.updateUI(currentTimeSeconds);
            // Tick UIUpdateScheduler for registered UI callbacks (Phase 6 timer consolidation)
            const scheduler = uiUpdateScheduler();
            if (scheduler) {
                scheduler.tick(deltaTime);
            }
            this.debugPanel.update();
            this.lastUIUpdateTime = currentTime;
        }

        // Check for WebGL context loss before rendering
        const gl = this.renderer.getContext();
        if (gl.isContextLost()) {
            // Log once per second to avoid spam
            if (!this._lastContextLostLog || currentTime - this._lastContextLostLog > 1000) {
                console.error('[Main] WebGL context is lost - cannot render');
                this._lastContextLostLog = currentTime;
            }
            return;
        }

        this.renderer.render(this.scene, this.camera);
    }

    startFallbackTimer() {
        if (this.fallbackTimerInterval) return; // Already running

        this.isUsingFallbackTimer = true;

        // Run game loop using setInterval when backgrounded (using constants)
        this.fallbackTimerInterval = setInterval(() => {
            this.animate();
        }, 1000 / TIMING.FALLBACK_FPS);
    }

    stopFallbackTimer() {
        if (!this.fallbackTimerInterval) return; // Not running

        clearInterval(this.fallbackTimerInterval);
        this.fallbackTimerInterval = null;
        this.isUsingFallbackTimer = false;

        // Restart requestAnimationFrame loop
        requestAnimationFrame(() => this.animate());
    }

    /**
     * Start periodic debug logging for long-running diagnostics
     * Logs every 5 minutes, keeps last 200 entries in localStorage
     */
    startDebugLogging() {
        // Clear any existing interval to prevent accumulation
        if (this.debugLogInterval) {
            clearInterval(this.debugLogInterval);
            this.debugLogInterval = null;
        }

        // Log immediately at start
        this.logDebugSnapshot('startup');

        // Log every 5 minutes
        this.debugLogInterval = setInterval(() => {
            this.logDebugSnapshot('interval');
        }, 5 * 60 * 1000);

        // Only add event listeners once (they persist across game restarts)
        if (this._debugListenersAdded) return;
        this._debugListenersAdded = true;

        // Also log on visibility change (when tab is hidden/shown)
        document.addEventListener('visibilitychange', () => {
            this.logDebugSnapshot(document.hidden ? 'tab-hidden' : 'tab-visible');
        });

        // Log before page unload and save session state for offline simulation
        window.addEventListener('beforeunload', () => {
            // Skip save if starting a new game (localStorage already cleared)
            if (window._skipSaveOnUnload) return;

            this.logDebugSnapshot('unload');
            this.saveDebugLog();

            // Save session state for offline simulation
            const currentMomentum = this.driveStateMachine?.momentumBonus || 0;
            this.gameState.saveSessionState(currentMomentum);

            // Force sync to cloud before closing (fire-and-forget)
            if (this.cloudSaveManager && this.accountManager?.isLinked()) {
                this.cloudSaveManager.forceLocalToCloud(this.gameState.state);
            }
        });

        // Also sync to cloud when tab becomes hidden (mobile backgrounding, tab switch)
        document.addEventListener('visibilitychange', () => {
            if (document.hidden && this.cloudSaveManager && this.accountManager?.isLinked()) {
                this.cloudSaveManager.forceLocalToCloud(this.gameState.state);
            }
        });

        console.log('[Debug] Long-running diagnostics started. Check localStorage "roadtripDebugLog" after a long session.');
    }

    /**
     * Capture a debug snapshot
     */
    logDebugSnapshot(trigger) {
        const snapshot = {
            timestamp: new Date().toISOString(),
            trigger: trigger,
            uptimeMinutes: Math.round((performance.now() / 1000 / 60) * 10) / 10,
            worldTranslator: this.worldTranslator ? this.worldTranslator.getStats() : null,
            biome: this.biomeManager?.currentBiome?.getDebugStats?.() || null,
            scene: {
                childCount: this.scene?.children?.length || 0,
                cameraZ: this.camera?.position?.z || 0
            },
            memory: performance.memory ? {
                usedJSHeapSize: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024),
                totalJSHeapSize: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024)
            } : null
        };

        this.debugLogHistory.push(snapshot);

        // Keep only last 200 entries
        if (this.debugLogHistory.length > 200) {
            this.debugLogHistory.shift();
        }

        // Save to localStorage periodically
        if (this.debugLogHistory.length % 10 === 0) {
            this.saveDebugLog();
        }

        // Console log for immediate visibility
        console.log('[Debug]', trigger, snapshot);
    }

    /**
     * Save debug log to localStorage
     */
    saveDebugLog() {
        try {
            localStorage.setItem('roadtripDebugLog', JSON.stringify(this.debugLogHistory));
        } catch (e) {
            console.warn('[Debug] Failed to save debug log:', e);
        }
    }

    /**
     * Get debug log from localStorage (call from console)
     */
    static getDebugLog() {
        try {
            const log = localStorage.getItem('roadtripDebugLog');
            return log ? JSON.parse(log) : [];
        } catch (e) {
            console.error('Failed to parse debug log:', e);
            return [];
        }
    }

    // NOTE: Debug panel creation and update moved to src/ui/DebugPanel.js
}

// Start the app
// we should move these constants to a config file, which also opens the door to editor support later

async function initApp() {
    try {
        // Update progress: 10%
        if (window.updateLoadingProgress) window.updateLoadingProgress(10);

        // Load game configs from JSON files
        const configs = await GameState.loadConfigs();

        // Update progress: 30%
        if (window.updateLoadingProgress) window.updateLoadingProgress(30);

        // Create app with configs (this loads models, textures, sounds)
        // NOTE: GameState is created but NOT initialized yet - we need to
        // determine the correct slot first
        const app = new RoadTripApp(configs);

        // Store app globally for start button
        window.roadTripApp = app;

        // === CRITICAL: Initialize auth and select correct slot ===
        // This MUST happen before any code that accesses game state data.
        // It waits for Firebase auth to resolve, then loads the correct slot's data.
        console.log('[Startup] === WAITING FOR AUTH AND SLOT ===');
        const { slot, isLinked } = await app.initializeAuthAndSlot();
        console.log('[Startup] Auth complete - slot:', slot, 'isLinked:', isLinked);

        // Update progress: 60% (auth resolved, state loaded)
        if (window.updateLoadingProgress) window.updateLoadingProgress(60);

        // Set up bug report buttons (now that we have gameState and accountManager)
        setupBugReportButtons({
            gameState: app.gameState,
            accountManager: app.accountManager
        });

        // Expose debug log helper globally for easy console access
        window.getDebugLog = RoadTripApp.getDebugLog;

        // Wait for app's async initialization to complete (biome/pool managers, UI)
        // This ensures 80% progress is reached before we continue
        await app.ready;

        // === STARTUP RECOVERY ===
        // Resume or start simulator for the CORRECT slot (now guaranteed)
        console.log('[Startup] === SIMULATOR RECOVERY ===');

        // Check if a backup restore is pending - skip normal recovery, let auth handler do it
        const backupLoadPending = localStorage.getItem('idlemile_backup_loaded') === 'true';
        if (backupLoadPending) {
            console.log('[Startup] Backup load pending - skipping normal recovery');
            // Clear old simulator state so it doesn't interfere
            localStorage.removeItem('offlineSimulatorState_roadTripSave_account');
            localStorage.removeItem('offlineSimulatorState_roadTripSave_anonymous');
            // Auth handler will handle the restore
        }

        const slotKey = app.gameState.saveManager.getActiveSaveKey();
        const hasSimulatorState = !backupLoadPending && localStorage.getItem(`offlineSimulatorState_${slotKey}`) !== null;
        const hasSaveGame = !backupLoadPending && app.gameState.saveManager.load() !== null;

        console.log('[Startup] Slot:', slotKey);
        console.log('[Startup] Has simulator state:', hasSimulatorState);
        console.log('[Startup] Has save game:', hasSaveGame);

        if (hasSimulatorState || hasSaveGame) {
            // Mark that startup handled the simulator - auth handler should skip
            window._startupRecoveryComplete = true;

            // Create simulator
            if (!app.offlineSimulator) {
                app.offlineSimulator = new OfflineSimulator(app.gameState);
            }

            // Wire up callbacks
            app.offlineDisplayManager.offlineSimulator = app.offlineSimulator;
            app.offlineSimulator.onUpdate = (progress) => {
                app.offlineDisplayManager?.updateDisplay(progress);
            };
            app.offlineSimulator.onFuelDepleted = () => {
                app.offlineDisplayManager?.handleFuelDepleted();
            };

            let started = false;

            // Try to resume from saved state first
            if (hasSimulatorState) {
                started = app.offlineSimulator.resume();
                console.log('[Startup] Resume from saved state:', started ? 'SUCCESS' : 'FAILED');
            }

            // If no saved state or resume failed, start fresh
            if (!started && app.gameState.getFuel() > 0) {
                const momentum = app.gameState.state?.session?.exitMomentum || 0;
                app.offlineSimulator.start(momentum);
                started = true;
                console.log('[Startup] Started fresh with momentum:', momentum);
            }

            // Always show offline display and set up tooltip
            app.offlineDisplayManager?.setupSpeedTooltip();
            app.offlineDisplayManager?.setupPrestigeBadge();
            // Check isRunning/isSuspended instead of started - resume() returns true even if fuel depleted
            if (app.offlineSimulator.isRunning || app.offlineSimulator.isSuspended) {
                app.offlineDisplayManager?.updateDisplay(app.offlineSimulator.getProgress());
            } else {
                // No fuel or fuel depleted during resume - show depleted state
                // Pass progress so that any accumulated offline progress is displayed
                console.log('[Startup] No fuel or depleted, showing depleted state');
                app.offlineDisplayManager?.showDepletedState(app.offlineSimulator?.getProgress());
            }
        }

        // All initialization complete - mark loading as 100%
        // This enables the DRIVE button only after offline calculations are done
        if (window.updateLoadingProgress) window.updateLoadingProgress(100);

        // Clear startup flag after delay so auth handler can handle future logins/logouts
        setTimeout(() => {
            console.log('[Startup] Clearing startup flag');
            delete window._startupRecoveryComplete;
        }, 3000);

    } catch (error) {
        console.error('Failed to initialize app:', error);
        document.body.innerHTML += '<div style="color: red; padding: 20px; position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; z-index: 1000; max-width: 80%;">Error initializing app:<br>' + error.message + '<br><br>Stack: ' + error.stack + '</div>';
    }
}

// Set up console error capture for bug reports (before anything else)
setupConsoleErrorCapture();

// Set up browser zoom prevention
setupZoomPrevention();

// Initialize start screen using StartScreenManager
const startScreenManager = new StartScreenManager();
startScreenManager.initialize(initApp);